/**
 * ADR-0007 — the Migration Center.
 *
 * Shared types and helpers for the import screens. Mirrors the shapes
 * `ImportsService` actually returns — read from
 * `Tarabeeza-Backend/src/modules/imports/*` — rather than a generated client,
 * because this module has not been added to `@/lib/shared-types` yet.
 *
 * Money is never parsed here. Every amount this module touches (a row's price,
 * a cost, a quantity) is either opaque to the dashboard (it lives inside the
 * uploaded file, which the server alone reads) or arrives already formatted in
 * a `RowIssue.value` string for display — there is no arithmetic on it client
 * side, on purpose.
 */
import { api } from '@/lib/api';

/**
 * `Badge`'s own `Tone` union is not exported, so this is a structural copy —
 * TypeScript checks it by shape, not by name, so it stays interchangeable with
 * `<Badge tone={...} />` as long as the two lists of literals agree.
 */
type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

export type ImportDatasetId =
  | 'MENU_ITEMS'
  | 'CUSTOMERS'
  | 'SUPPLIERS'
  | 'INVENTORY_ITEMS'
  | 'OPENING_STOCK';

export type ImportFieldType = 'text' | 'decimal' | 'boolean' | 'phone' | 'email' | 'percent' | 'enum';

export interface ImportFieldSpec {
  key: string;
  type: ImportFieldType;
  required: boolean;
  label: string;
  labelAr: string;
  values?: string[];
  hint?: string;
}

export interface ImportDatasetSpec {
  dataset: ImportDatasetId;
  label: string;
  labelAr: string;
  /** The field(s) a row is matched on — the first one mapped is what re-import keys to. */
  matchedOn: string[];
  commitPermissions: string[];
  fields: ImportFieldSpec[];
}

export type ImportJobStatus =
  | 'UPLOADED'
  | 'VALIDATED'
  | 'INVALID'
  | 'COMMITTING'
  | 'COMMITTED'
  | 'PARTIALLY_COMMITTED'
  | 'FAILED'
  | 'CANCELLED';

export interface JobTotals {
  rows: number;
  valid: number;
  invalid: number;
  created: number;
  updated: number;
  unchanged: number;
  committedRows: number;
}

export type IssueSeverity = 'ERROR' | 'WARNING';

export interface RowIssue {
  line: number;
  column?: string | null;
  field?: string | null;
  code: string;
  message: string;
  value?: string | null;
  severity: IssueSeverity;
}

export interface ImportJobRow {
  id: string;
  dataset: ImportDatasetId;
  status: ImportJobStatus;
  fileName: string;
  fileSize: number;
  rowCount: number;
  errorCount: number;
  warningCount: number;
  totals: JobTotals | null;
  failureReason: string | null;
  validatedAt: string | null;
  committedAt: string | null;
  createdAt: string;
  createdBy: { id: string; firstName: string | null; lastName: string | null } | null;
}

export type ColumnMapping = Record<string, string | null>;

export interface ImportOptions {
  warehouseCode?: string;
  defaultUnitCode?: string;
}

export interface ImportJobDetail extends ImportJobRow {
  sheetName: string | null;
  columns: string[];
  mapping: ColumnMapping | null;
  options: ImportOptions | null;
  issues: RowIssue[];
}

export interface UploadResult {
  id: string;
  dataset: ImportDatasetId;
  status: ImportJobStatus;
  fileName: string;
  sheetName: string | null;
  columns: string[];
  rowCount: number;
  suggestedMapping: ColumnMapping;
  unmapped: string[];
  sample: { line: number; cells: Record<string, string | null> }[];
}

export interface ValidateResult {
  jobId: string;
  dataset: ImportDatasetId;
  status: ImportJobStatus;
  totals: JobTotals;
  errorCount: number;
  warningCount: number;
  truncated: boolean;
  issues: RowIssue[];
}

export interface CommitResult {
  jobId: string;
  status: ImportJobStatus;
  totals: JobTotals;
  alreadyCommitted: boolean;
}

export interface DatasetsResponse {
  datasets: ImportDatasetSpec[];
  /** Datasets this actor could actually land — see `ImportsService.committableDatasets`. */
  committable: ImportDatasetId[];
  limits: { maxUploadBytes: number };
}

/** Surface the API's own message rather than a generic "something went wrong". */
export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.join(' · ');
  return message ?? fallback;
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * A dataset field carries its own English and Arabic label — `label`/`labelAr`
 * off `ImportDatasetSpec` — because these are the operator's own column names,
 * not app chrome, and belong with the data the same way an item's own
 * translations do.
 */
export function localize(en: string, ar: string, language: string): string {
  return language.startsWith('ar') ? ar : en;
}

const STATUS_TONE: Record<ImportJobStatus, Tone> = {
  UPLOADED: 'neutral',
  VALIDATED: 'info',
  INVALID: 'warning',
  COMMITTING: 'info',
  COMMITTED: 'success',
  PARTIALLY_COMMITTED: 'warning',
  FAILED: 'danger',
  CANCELLED: 'neutral',
};

export function toneForJobStatus(status: ImportJobStatus): Tone {
  return STATUS_TONE[status] ?? 'neutral';
}

/** Whether committing has already been attempted at least once. */
export function hasAttemptedCommit(status: ImportJobStatus): boolean {
  return status === 'PARTIALLY_COMMITTED' || status === 'FAILED' || status === 'COMMITTED';
}

/**
 * `ImportsService.cancel` throws for `COMMITTED`, `PARTIALLY_COMMITTED` and
 * `COMMITTING` — anything that has written a row, or is writing one right now.
 * Mirrored here so the button is never offered only to 409.
 */
export function canCancelJob(status: ImportJobStatus): boolean {
  return status === 'UPLOADED' || status === 'VALIDATED' || status === 'INVALID' || status === 'FAILED';
}

/**
 * Whether the mapping table (and the "Check this file" button) are open for
 * editing. `PARTIALLY_COMMITTED` is deliberately excluded even though it is
 * resumable — `ImportsService.validate` refuses to re-validate a job that is
 * half committed, because the rows already written were written under the
 * mapping as it stood then, and editing it now would move the goalposts
 * under a commit that is already in flight.
 */
export function canEditMapping(status: ImportJobStatus): boolean {
  return (
    status === 'UPLOADED' || status === 'VALIDATED' || status === 'INVALID' || status === 'FAILED'
  );
}

export async function fetchDatasets(): Promise<DatasetsResponse> {
  const { data } = await api.get<DatasetsResponse>('/imports/datasets');
  return data;
}

export async function fetchTemplate(dataset: ImportDatasetId): Promise<Blob> {
  const { data } = await api.get(`/imports/datasets/${dataset}/template`, { responseType: 'blob' });
  return data as Blob;
}

export async function uploadFile(dataset: ImportDatasetId, file: File): Promise<UploadResult> {
  const body = new FormData();
  body.append('file', file);
  const { data } = await api.post<UploadResult>(`/imports/${dataset}`, body);
  return data;
}

export async function fetchJob(id: string): Promise<ImportJobDetail> {
  const { data } = await api.get<ImportJobDetail>(`/imports/${id}`);
  return data;
}

export async function validateJob(
  id: string,
  mapping: ColumnMapping,
  options: ImportOptions,
  timeoutMs?: number,
): Promise<ValidateResult> {
  const { data } = await api.post<ValidateResult>(
    `/imports/${id}/validate`,
    { mapping, options },
    timeoutMs ? { timeout: timeoutMs } : undefined,
  );
  return data;
}

export async function commitJob(id: string, timeoutMs?: number): Promise<CommitResult> {
  const { data } = await api.post<CommitResult>(
    `/imports/${id}/commit`,
    undefined,
    timeoutMs ? { timeout: timeoutMs } : undefined,
  );
  return data;
}

export async function cancelJob(id: string): Promise<{ jobId: string; status: ImportJobStatus }> {
  const { data } = await api.post<{ jobId: string; status: ImportJobStatus }>(`/imports/${id}/cancel`);
  return data;
}

export async function fetchErrorReport(id: string): Promise<{ fileName: string; blob: Blob }> {
  const response = await api.get(`/imports/${id}/errors.csv`, { responseType: 'blob' });
  const headers = response.headers as Record<string, unknown>;
  const disposition = headers?.['content-disposition'];
  const match = typeof disposition === 'string' ? /filename="([^"]+)"/.exec(disposition) : null;
  return { fileName: match?.[1] ?? `${id}-errors.csv`, blob: response.data as Blob };
}

/** Triggers a browser download of an in-memory blob, then frees it. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}
